import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  History, TrendingUp, TrendingDown, Filter, RefreshCw,
  User, Calendar, Package, Factory, Zap, Users, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ENTITY_LABELS = {
  finished_good: { label: 'Finished Good', icon: Package, color: 'text-blue-400', bg: 'bg-blue-900/40 border-blue-700' },
  raw_material: { label: 'Raw Material', icon: Layers, color: 'text-amber-400', bg: 'bg-amber-900/40 border-amber-700' },
  purchase_bill: { label: 'Purchase Rate', icon: Factory, color: 'text-purple-400', bg: 'bg-purple-900/40 border-purple-700' },
  expense: { label: 'Expense', icon: Zap, color: 'text-rose-400', bg: 'bg-rose-900/40 border-rose-700' },
  rate_card: { label: 'Rate Card', icon: Users, color: 'text-emerald-400', bg: 'bg-emerald-900/40 border-emerald-700' },
  other: { label: 'Other', icon: History, color: 'text-zinc-400', bg: 'bg-zinc-800 border-zinc-700' },
};

function fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (!Number.isNaN(n)) return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  return String(v);
}
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso.slice(0, 16); }
}

export default function PriceAuditLog() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entityType, setEntityType] = useState('');
  const [days, setDays] = useState(90);
  const [manualOpen, setManualOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, sumRes] = await Promise.all([
        axios.get(`${API}/price-audit`, {
          params: { days, ...(entityType ? { entity_type: entityType } : {}) },
          withCredentials: true,
        }),
        axios.get(`${API}/price-audit/summary`, { params: { days }, withCredentials: true }),
      ]);
      setRows(listRes.data?.items || []);
      setSummary(sumRes.data || null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityType, days]);

  const kpiTiles = useMemo(() => {
    const byType = summary?.by_entity_type || {};
    return [
      { key: 'finished_good', label: 'FG Price Changes', value: byType.finished_good || 0 },
      { key: 'raw_material', label: 'RM Rate Changes', value: byType.raw_material || 0 },
      { key: 'expense', label: 'Expense Changes', value: byType.expense || 0 },
      { key: 'total', label: `Total (last ${days}d)`, value: summary?.total_changes || 0 },
    ];
  }, [summary, days]);

  return (
    <div data-testid="price-audit-page" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-zinc-100 flex items-center gap-2">
            <History className="text-amber-400" size={24} /> Price Audit Log
          </h1>
          <p className="text-sm text-zinc-400 mt-1">Who changed what rate, when — across RM, FG, expenses, and rate cards.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="audit-add-manual"
            onClick={() => setManualOpen(true)}
            className="px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700"
          >+ Manual Entry</button>
          <button
            data-testid="audit-refresh"
            onClick={load}
            className="p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700"
            title="Refresh"
          ><RefreshCw size={14} /></button>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {kpiTiles.map(t => (
          <div
            key={t.key}
            data-testid={`audit-kpi-${t.key}`}
            className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4"
          >
            <div className="text-xs text-zinc-400">{t.label}</div>
            <div className="text-2xl font-bold text-zinc-100 mt-1">{t.value}</div>
          </div>
        ))}
      </div>

      {/* Biggest swings */}
      {summary && (summary.top_increases?.length || summary.top_decreases?.length) ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3 text-zinc-100 font-semibold">
              <TrendingUp size={16} className="text-red-400" /> Biggest Increases
            </div>
            {(summary.top_increases || []).length === 0
              ? <div className="text-xs text-zinc-500">No increases in this window.</div>
              : (summary.top_increases || []).map((r, i) => (
                <div key={i} className="text-xs text-zinc-300 py-1.5 border-b border-zinc-800 last:border-b-0">
                  <span className="font-medium">{r.entity_name}</span> · <span className="text-zinc-500">{r.field}</span>:
                  <span className="text-zinc-400 ml-1">{fmt(r.old_value)} → {fmt(r.new_value)}</span>
                  <span className="text-red-400 ml-2 font-semibold">+{r.pct_change}%</span>
                </div>
              ))}
          </div>
          <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3 text-zinc-100 font-semibold">
              <TrendingDown size={16} className="text-green-400" /> Biggest Decreases
            </div>
            {(summary.top_decreases || []).length === 0
              ? <div className="text-xs text-zinc-500">No decreases in this window.</div>
              : (summary.top_decreases || []).map((r, i) => (
                <div key={i} className="text-xs text-zinc-300 py-1.5 border-b border-zinc-800 last:border-b-0">
                  <span className="font-medium">{r.entity_name}</span> · <span className="text-zinc-500">{r.field}</span>:
                  <span className="text-zinc-400 ml-1">{fmt(r.old_value)} → {fmt(r.new_value)}</span>
                  <span className="text-green-400 ml-2 font-semibold">{r.pct_change}%</span>
                </div>
              ))}
          </div>
        </div>
      ) : null}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={14} className="text-zinc-500" />
        <select
          data-testid="audit-filter-type"
          value={entityType}
          onChange={e => setEntityType(e.target.value)}
          className="bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
        >
          <option value="">All types</option>
          {Object.entries(ENTITY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </select>
        <select
          data-testid="audit-filter-days"
          value={days}
          onChange={e => setDays(parseInt(e.target.value))}
          className="bg-zinc-800 border border-zinc-700 text-zinc-100 text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:border-amber-500"
        >
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
        <span className="text-xs text-zinc-500 ml-2">{rows.length} entries</span>
      </div>

      {/* Audit table */}
      <div className="bg-zinc-900/70 border border-zinc-800 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-zinc-500 text-sm animate-pulse">Loading audit log…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">No price changes recorded for the selected filter.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs text-zinc-300">
              <thead className="bg-zinc-800/60 text-zinc-400 uppercase text-[10px]">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Entity</th>
                  <th className="px-3 py-2 text-left">Field</th>
                  <th className="px-3 py-2 text-right">Old</th>
                  <th className="px-3 py-2 text-right">New</th>
                  <th className="px-3 py-2 text-right">Δ%</th>
                  <th className="px-3 py-2 text-left">By</th>
                  <th className="px-3 py-2 text-left">Reason</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const meta = ENTITY_LABELS[r.entity_type] || ENTITY_LABELS.other;
                  const Icon = meta.icon;
                  const deltaClass = r.pct_change > 0 ? 'text-red-400' : r.pct_change < 0 ? 'text-green-400' : 'text-zinc-500';
                  return (
                    <tr
                      key={r.id}
                      data-testid={`audit-row-${i}`}
                      className="border-t border-zinc-800 hover:bg-zinc-800/40"
                    >
                      <td className="px-3 py-2 text-zinc-400 whitespace-nowrap"><Calendar size={11} className="inline mr-1" />{fmtDate(r.created_at)}</td>
                      <td className="px-3 py-2"><span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] border ${meta.bg} ${meta.color}`}><Icon size={10} />{meta.label}</span></td>
                      <td className="px-3 py-2 font-medium text-zinc-100">{r.entity_name || '—'}</td>
                      <td className="px-3 py-2 text-zinc-400">{r.field}</td>
                      <td className="px-3 py-2 text-right text-zinc-400">{fmt(r.old_value)}</td>
                      <td className="px-3 py-2 text-right text-zinc-100 font-semibold">{fmt(r.new_value)}</td>
                      <td className={`px-3 py-2 text-right font-semibold ${deltaClass}`}>{r.pct_change !== null && r.pct_change !== undefined ? `${r.pct_change > 0 ? '+' : ''}${r.pct_change}%` : '—'}</td>
                      <td className="px-3 py-2 text-zinc-400"><User size={11} className="inline mr-1" />{r.user_name || r.user_email || '—'}</td>
                      <td className="px-3 py-2 text-zinc-500">{r.reason || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {manualOpen && <ManualEntryModal onClose={() => setManualOpen(false)} onSaved={() => { setManualOpen(false); load(); }} />}
    </div>
  );
}

function ManualEntryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({
    entity_type: 'expense',
    entity_name: '',
    field: 'monthly_cost',
    old_value: '',
    new_value: '',
    reason: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.entity_name || !form.field || form.old_value === '' || form.new_value === '') {
      toast.error('Fill all required fields'); return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/price-audit/manual`, form, { withCredentials: true });
      toast.success('Audit entry recorded');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="audit-manual-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 w-full max-w-md">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-zinc-100">Record Price Change</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-200">✕</button>
        </div>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <label className="text-xs text-zinc-400">Type</label>
            <select
              data-testid="manual-type"
              value={form.entity_type}
              onChange={e => setForm({ ...form, entity_type: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
            >
              {Object.entries(ENTITY_LABELS).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
            </select>
          </div>
          <div>
            <label className="text-xs text-zinc-400">Name (e.g. "Electricity Bill", "Labour Rate")</label>
            <input
              data-testid="manual-name"
              value={form.entity_name}
              onChange={e => setForm({ ...form, entity_name: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
              required
            />
          </div>
          <div>
            <label className="text-xs text-zinc-400">Field (e.g. "rate_per_unit", "monthly_cost")</label>
            <input
              data-testid="manual-field"
              value={form.field}
              onChange={e => setForm({ ...form, field: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-zinc-400">Old Value</label>
              <input
                data-testid="manual-old"
                type="number" step="any"
                value={form.old_value}
                onChange={e => setForm({ ...form, old_value: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
              />
            </div>
            <div>
              <label className="text-xs text-zinc-400">New Value</label>
              <input
                data-testid="manual-new"
                type="number" step="any"
                value={form.new_value}
                onChange={e => setForm({ ...form, new_value: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-zinc-400">Reason / Notes</label>
            <input
              data-testid="manual-reason"
              value={form.reason}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-zinc-100 mt-1"
              placeholder="e.g. Annual hike, supplier price revision"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button" onClick={onClose}
              className="flex-1 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-sm"
            >Cancel</button>
            <button
              data-testid="manual-save"
              type="submit" disabled={saving}
              className="flex-1 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 disabled:bg-zinc-700 text-white text-sm font-medium"
            >{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
